import React from 'react';
import { 
  CheckCircleIcon,
  HashtagIcon,
  CubeIcon,
  TrashIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

/**
 * OperationType - The type of operation that was performed
 */
export type OperationType = 'update_quantity' | 'change_product' | 'clear_tag' | 'replace_tag';

/**
 * OperationDetails - Details about the operation that was performed
 */
export interface OperationDetails {
  type: OperationType;
  tagNumber: string;
  productSku?: string;
  productName?: string;
  oldValue?: string | number;
  newValue?: string | number;
  oldProductSku?: string;
  oldProductName?: string;
  newProductSku?: string;
  newProductName?: string;
}

/**
 * ManageTagsConfirmationModalProps - Props for the confirmation modal
 */
interface ManageTagsConfirmationModalProps {
  isOpen: boolean;
  onSearchMore: () => void;
  onViewUpdatedTag: () => void;
  operationDetails: OperationDetails | null;
}

/**
 * Get icon for operation type
 */
const getOperationIcon = (type: OperationType) => {
  switch (type) {
    case 'update_quantity':
      return <HashtagIcon className="h-6 w-6 text-success-600" />;
    case 'change_product':
      return <CubeIcon className="h-6 w-6 text-success-600" />;
    case 'clear_tag':
      return <TrashIcon className="h-6 w-6 text-success-600" />;
    case 'replace_tag':
      return <ArrowPathIcon className="h-6 w-6 text-success-600" />;
    default:
      return <CheckCircleIcon className="h-6 w-6 text-success-600" />;
  }
};

/**
 * Get title for operation type
 */
const getOperationTitle = (type: OperationType) => {
  switch (type) {
    case 'update_quantity':
      return 'Package Quantity Updated Successfully!';
    case 'change_product':
      return 'Product Association Changed Successfully!';
    case 'clear_tag':
      return 'Tag Data Cleared Successfully!';
    case 'replace_tag':
      return 'RFID Tag Replaced Successfully!';
    default:
      return 'Operation Completed Successfully!';
  }
};

/**
 * ManageTagsConfirmationModal Component
 * Shows a success message after tag management operations
 */
const ManageTagsConfirmationModal: React.FC<ManageTagsConfirmationModalProps> = ({
  isOpen,
  onSearchMore,
  onViewUpdatedTag,
  operationDetails
}) => {
  if (!isOpen || !operationDetails) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
        
        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
          <div className="text-center">
            {/* Success icon */}
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-success-100 mb-4">
              {getOperationIcon(operationDetails.type)}
            </div>
            
            {/* Success title */}
            <h3 className="text-lg leading-6 font-medium text-secondary-900 mb-2">
              {getOperationTitle(operationDetails.type)}
            </h3>
            
            {/* Operation details */}
            <div className="bg-success-50 rounded-lg p-4 mb-6">
              {/* Tag number */}
              <div className="mb-3">
                <span className="text-sm font-mono text-success-900">
                  Tag: {operationDetails.tagNumber}
                </span>
              </div>

              {/* Operation-specific details */}
              {operationDetails.type === 'update_quantity' && (
                <div className="space-y-2">
                  {operationDetails.productSku && (
                    <div className="text-sm text-success-800">
                      <span className="font-medium">Product:</span> {operationDetails.productSku}
                      {operationDetails.productName && <span className="ml-1">- {operationDetails.productName}</span>}
                    </div>
                  )}
                  <div className="flex items-center justify-center space-x-2 text-sm text-success-800">
                    <span className="font-medium">Quantity:</span>
                    <span className="bg-white px-2 py-1 rounded">{operationDetails.oldValue}</span>
                    <span>→</span>
                    <span className="bg-success-200 px-2 py-1 rounded font-medium">{operationDetails.newValue}</span>
                  </div>
                </div>
              )}

              {operationDetails.type === 'change_product' && (
                <div className="space-y-2">
                  <div className="text-sm text-success-800">
                    <span className="font-medium">Product Changed:</span>
                  </div>
                  <div className="flex flex-col space-y-2 text-sm">
                    <div className="bg-white p-2 rounded">
                      <span className="text-secondary-600">From:</span>
                      <div className="font-medium text-secondary-800">
                        {operationDetails.oldProductSku}
                        {operationDetails.oldProductName && <div className="text-xs">{operationDetails.oldProductName}</div>}
                      </div>
                    </div>
                    <div className="bg-success-200 p-2 rounded">
                      <span className="text-success-700">To:</span>
                      <div className="font-medium text-success-900">
                        {operationDetails.newProductSku}
                        {operationDetails.newProductName && <div className="text-xs">{operationDetails.newProductName}</div>}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {operationDetails.type === 'clear_tag' && (
                <div className="space-y-2">
                  <div className="text-sm text-success-800">
                    Tag has been cleared and is now available for reuse
                  </div>
                  {operationDetails.productSku && (
                    <div className="text-sm text-success-700">
                      <span className="font-medium">Previously matched to:</span>
                      <div className="mt-1">
                        {operationDetails.productSku}
                        {operationDetails.productName && <span className="ml-1">- {operationDetails.productName}</span>}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {operationDetails.type === 'replace_tag' && (
                <div className="space-y-2">
                  <div className="flex items-center justify-center space-x-2 text-sm text-success-800">
                    <span className="font-medium">RFID:</span>
                    <span className="bg-white px-2 py-1 rounded font-mono text-xs">{operationDetails.oldValue}</span>
                    <span>→</span>
                    <span className="bg-success-200 px-2 py-1 rounded font-mono text-xs">{operationDetails.newValue}</span>
                  </div>
                  {operationDetails.productSku && (
                    <div className="text-sm text-success-700 mt-2">
                      <span className="font-medium">Product remains:</span> {operationDetails.productSku}
                      {operationDetails.productName && <span className="ml-1">- {operationDetails.productName}</span>}
                    </div>
                  )}
                  <div className="text-xs text-warning-600 mt-2">
                    Old tag ({operationDetails.oldValue}) is no longer available for use and must be discarded
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action prompt */}
          <div className="text-center mb-6">
            <p className="text-sm text-secondary-600">
              What would you like to do next?
            </p>
          </div>

          {/* Action buttons */}
          {operationDetails.type === 'clear_tag' ? (
            // For cleared tags, only show "Search More Tags" button
            <div className="flex justify-center">
              <button
                type="button"
                onClick={onSearchMore}
                className="w-full sm:w-auto px-6 py-2 inline-flex justify-center rounded-md border border-transparent shadow-sm bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:text-sm"
              >
                Search More Tags
              </button>
            </div>
          ) : (
            // For other operations, show both buttons
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onSearchMore}
                className="flex-1 inline-flex justify-center rounded-md border border-secondary-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-secondary-700 hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:text-sm"
              >
                Search More Tags
              </button>
              
              <button
                type="button"
                onClick={onViewUpdatedTag}
                className="flex-1 inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:text-sm"
              >
                View Updated Tag
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageTagsConfirmationModal;